import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { mergeExistingFields } from "helpers/mergeExistingFields";

export interface MirrorItem {
  id: string;
  domain: string;
  title: string;
  status: 'created' | 'removed';
  priority: 'low' | 'medium' | 'high';
  record: string;
  api: string;
  cdn: string;
  countries: string[];
  isDefault: number;
}

export interface MirrorState {
  list: MirrorItem[];
  isListFetched: boolean;
  isItemFetched: boolean;
  activePage: number;
  totalItems: number;
  totalPages: number;
  selectedItems: string[];
  draftItem?: Partial<MirrorItem>;
  processingItemId?: MirrorItem['id'];
  isItemFetching: boolean;
}

export type OrderFields = Extract<'domain' | 'title' | 'priority' | 'status', keyof MirrorItem>;

export interface GetMirrorListPayload {
  limit: number;
  offset: number;
  title?: string;
  domain?: string;
  order?: {
    [k in OrderFields]?: 'asc' | 'desc' | 'none';
  },
  priorities?: string[];
}

export interface MirrorListResponse {
  items: MirrorItem[];
  totalItems: number;
  totalPages: number;
}

const getMirrorListPayload = createAsyncThunk(
  'mirror/list',
  async (data: GetMirrorListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.mirrorList}`, { ...data });

      return result.data as MirrorListResponse;
    } catch (error) {
      return undefined;
    }
  },
);

export type SaveMirrorPayload = Pick<MirrorItem, 'title' | 'domain' | 'priority'> & {
  destinationCountries: string[];
};

const toMirrorApiError = (error: any) => {
  const data = error?.data ?? error?.response?.data ?? error;

  if (Array.isArray(data?.errors)) {
    return {
      code: "BusinessError",
      fieldErrors: [],
      globalErrors: data.errors.map((item: { message: string }) => ({
        code: item.message,
      })),
    };
  }

  if (typeof data?.message === "string") {
    return {
      code: "BusinessError",
      fieldErrors: [],
      globalErrors: [{ code: data.message }],
    };
  }

  return {
    code: "InternalError",
    fieldErrors: [],
    globalErrors: [{ code: "unknownError" }],
  };
};

const createMirrorItem = createAsyncThunk(
  'mirror/create',
  async (data: SaveMirrorPayload, { rejectWithValue }) => {
    try {
      const result = await instance.post(apiRoutes.mirrorCreate, data);
      return result.data as MirrorItem;
    } catch (error) {
      return rejectWithValue(toMirrorApiError(error));
    }
  },
);

const updateMirrorItem = createAsyncThunk(
  'mirror/update',
  async (data: SaveMirrorPayload, { rejectWithValue }) => {
    try {
      const result = await instance.put(apiRoutes.mirrorUpdate, data);
      return result.data as MirrorItem;
    } catch (error) {
      return rejectWithValue(toMirrorApiError(error));
    }
  },
);

const getMirrorItem = createAsyncThunk('mirror/item', async (domain: string, { getState }) => {
  const { mirror } = getState() as { mirror: MirrorState };
  const cached = mirror.list.find((item) => item.domain === domain || item.id === domain);
  if (cached) return cached;
  const result = await instance.post(apiRoutes.mirrorList, { limit: 20, offset: 0, domain });
  const item = (result.data as MirrorListResponse).items.find((item) => item.domain === domain || item.id === domain);
  if (!item) throw new Error('Mirror not found');
  return item;
});

const disableMirrorItem = createAsyncThunk(
  'mirror/disable',
  async (id: string) => {
    try {
      await instance.put(`${apiRoutes.mirrorDisable}`, { id });
      return true;
    } catch (error) {
      throw error;
    }
  }
);

const enableMirrorItem = createAsyncThunk(
  'mirror/enable',
  async (id: string) => {
    try {
      await instance.put(`${apiRoutes.mirrorEnable}`, { id });
      return true;
    } catch (error) {
      throw error;
    }
  }
);

const setMirrorAsDefault = createAsyncThunk(
  'mirror/set-default',
  async (id: string) => {
    try {
      await instance.put(`${apiRoutes.mirrorSetDefault}`, { id });
      return true;
    } catch (error) {
      throw error;
    }
  }
)

const initialState: MirrorState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  processingItemId: '',
  isItemFetching: false
};

const mirrorSlice = createSlice({
  name: 'mirror',
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectAll: (state) => {
      state.selectedItems = state.selectedItems.length !== state.list.length
        ? state.list.map((item) => item.record)
        : [];
    },
    handleSelect: (state, action: PayloadAction<string>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        )
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleClearDraftItem: (state) => {
      state.draftItem = undefined;
      state.isItemFetched = false;
    },
    handleUpdateDraftItem: (state, action: PayloadAction<Partial<MirrorItem>>) => {
      state.draftItem = { ...state.draftItem, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getMirrorListPayload.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createMirrorItem.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createMirrorItem.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createMirrorItem.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateMirrorItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId =
        state.list.find((item) => item.domain === action.meta.arg.domain)?.id;
    });
    builder.addCase(updateMirrorItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.domain === action.payload.domain
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = '';
    });
    builder.addCase(updateMirrorItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = '';
    });
    builder.addCase(getMirrorItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.draftItem = action.payload;
    });
    builder.addCase(disableMirrorItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(disableMirrorItem.fulfilled, (state) => {
      state.processingItemId = '';
      state.isListFetched = false;
    });
    builder.addCase(disableMirrorItem.rejected, (state) => {
      state.processingItemId = '';
    });
    builder.addCase(enableMirrorItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(enableMirrorItem.fulfilled, (state) => {
      state.processingItemId = '';
      state.isListFetched = false;
    });
    builder.addCase(enableMirrorItem.rejected, (state) => {
      state.processingItemId = '';
    });
    builder.addCase(setMirrorAsDefault.pending, (state, action) => {
      state.processingItemId = action.meta.arg ;
    });
    builder.addCase(setMirrorAsDefault.fulfilled, (state) => {
      state.processingItemId = '';
      state.isListFetched = false;
    });
    builder.addCase(setMirrorAsDefault.rejected, (state) => {
      state.processingItemId = '';
    });
  }
});


const {
  handlePaginate,
  handleSelectAll,
  handleSelect,
  handleClearDraftItem,
  handleUpdateDraftItem
} = mirrorSlice.actions;

export {
  mirrorSlice,
  handlePaginate,
  handleSelectAll,
  handleSelect,
  getMirrorListPayload,
  createMirrorItem,
  updateMirrorItem,
  getMirrorItem,
  handleClearDraftItem,
  handleUpdateDraftItem,
  disableMirrorItem,
  enableMirrorItem,
  setMirrorAsDefault
};
